import {SwipeController, SwipeLineOrientation} from "@luciad/ria/view/controller/SwipeController";
import {Map} from "@luciad/ria/view/Map";
import React, {useEffect, useRef, useState} from "react";
import {ControllerInstructions} from "./ControllerInstructions";
import {LayerPicker} from "./LayerPicker";
import {CursorHandle} from "@luciad/ria/view/CursorManager";
import "./SwipeUI.css";

interface Props {
  map: Map;
  controller: SwipeController;
}

export const SwipeUI = ({map, controller}: Props) => {
  const [isInstructionsVisible, setInstructionsVisible] = useState(
      controller.swipeLineOrientation === SwipeLineOrientation.VERTICAL);
  const [isVertical, setVertical] = useState(true);
  const [centerPos, setCenterPos] = useState(map.viewSize[0] / 2);
  const [isHovering, setHovering] = useState(false);
  const cursorHandleRef = useRef<CursorHandle | null>(null);

  useEffect(() => {
    function handleSwipeLineChange() {
      const {swipeLineOrientation, swipeLineLocation} = controller;
      const vertical = swipeLineOrientation === SwipeLineOrientation.VERTICAL;
      if (vertical) {
        setCenterPos(swipeLineLocation.x);
      } else {
        setCenterPos(swipeLineLocation.y);
      }
      setVertical(vertical);
    }

    function updateHovering() {
      setHovering(controller.swiping || controller.hovering);
    }

    const initialSwipeListener = controller.on("SwipingChanged", () => {
      setInstructionsVisible(false);
      initialSwipeListener.remove();
    });

    const orientationChangedListener = controller.on("SwipeLineOrientationChange", (orientation) => {
      setVertical(orientation === SwipeLineOrientation.VERTICAL);
      handleSwipeLineChange();
    });

    const swipeLocationListener = controller.on("SwipeLineLocationChange", handleSwipeLineChange);
    handleSwipeLineChange();

    const hoverChangeListener = controller.on("HoveringChanged", updateHovering);
    const swipingChangeListener = controller.on("SwipingChanged", updateHovering);
    updateHovering();

    return () => {
      initialSwipeListener.remove();
      orientationChangedListener.remove();
      swipeLocationListener.remove();
      hoverChangeListener.remove();
      swipingChangeListener.remove();
    }
  }, [map, controller]);

  useEffect(() => {
    if (isHovering) {
      const cursor = isVertical ? "col-resize" : "row-resize";
      if (cursorHandleRef.current?.style !== cursor) {
        cursorHandleRef.current = map.cursorManager.addCursor(cursor);
        return () => {
          cursorHandleRef.current?.remove();
          cursorHandleRef.current = null;
        }
      }
    }
  }, [isHovering, isVertical])

  return (
      <>
        <ControllerInstructions visible={isInstructionsVisible}>
          Left drag the swipe line horizontally or vertically <br/>
          Select layers at the top of the map.
        </ControllerInstructions>
        <LayerPicker
            map={map}
            controller={controller}
            isVertical={isVertical}
            centerPos={centerPos}
            leftContainerClass={isVertical ? "swipe-vertical" : "swipe-horizontal"}
            centralContainerChild={
              <div
                  className={
                    "swipe-line " + (isVertical ? "swipe-vertical" : "swipe-horizontal")
                  }
              />
            }
            rightContainerClass={isVertical ? "swipe-vertical" : "swipe-horizontal"}
        />
      </>
  );

}
